import { motion } from 'framer-motion';
import { useStore } from '@nanostores/react';
import { AccessibleModal } from '~/components/account/AccessibleModal';
import { $planWelcome, dismissPlanWelcome } from '~/lib/stores/planWelcome';
import { formatCredits } from '~/lib/plans-model';

/**
 * Global plan-activation modal. While a fresh payment is settling it shows a
 * "confirming" state (driven by `$planWelcome`); once the webhook activates the plan
 * it morphs into the celebration. In-place upgrades jump straight to the celebration.
 */
export function PlanWelcomeModal() {
  const state = useStore($planWelcome);

  if (!state) {
    return null;
  }

  // ── Confirming: payment done, webhook pending (~3–5s) ──
  if (state.status === 'confirming') {
    return (
      <AccessibleModal
        title="Confirming your payment"
        description="Setting up your plan — this only takes a few seconds."
        maxWidth={400}
        autoFocus={false}
        onClose={dismissPlanWelcome}
      >
        <div className="flex flex-col items-center px-4 pb-2 pt-3 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl border border-accent/25 bg-accent/10">
            <span className="i-svg-spinners:90-ring-with-bg text-[26px] text-accent" />
          </div>
          <h3 className="ui-serif m-0 text-[21px] leading-tight tracking-[-0.01em] text-primary">
            Setting up your plan
          </h3>
          <p className="mx-auto mt-2 max-w-[290px] text-[12.5px] leading-[1.55] text-secondary">
            Your payment went through — we're activating your plan and credits now. This usually takes just a few
            seconds.
          </p>
          <button type="button" onClick={dismissPlanWelcome} className="ui-btn-ghost mt-4 text-[12px] text-muted">
            Continue in the background
          </button>
        </div>
      </AccessibleModal>
    );
  }

  // ── Active: plan is live — celebrate ──
  const { name, credits, interval } = state;
  const isAnnual = interval === 'year';

  return (
    <AccessibleModal
      title={`Welcome to ${name}`}
      description={`Your ${name} plan is now active.`}
      maxWidth={440}
      autoFocus={false}
      onClose={dismissPlanWelcome}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
      >
        {/* Celebration header */}
        <div className="relative overflow-hidden rounded-xl border border-accent/20 bg-accent/[0.06] px-6 pb-6 pt-7 text-center">
          {/* Soft accent glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 -top-16 mx-auto h-40 w-40 rounded-full bg-accent/20 blur-3xl"
          />
          <div className="relative">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-accent/30 bg-accent/15">
              <span className="i-ph:confetti text-[30px] text-accent" />
            </div>
            <div className="ui-caption mb-1.5 text-accent">You're all set</div>
            <h3 className="ui-serif m-0 text-[27px] leading-[1.1] tracking-[-0.015em] text-primary">
              Welcome to {name}
            </h3>
            <p className="mx-auto mt-2 max-w-[300px] text-[13px] leading-[1.55] text-secondary">
              Your upgrade is live. You've got{' '}
              <span className="font-medium text-primary">{formatCredits(credits)} credits every month</span>
              {isAnnual ? ', billed annually,' : ''} to build with.
            </p>
          </div>
        </div>

        {/* Perks */}
        <div className="mt-3.5 flex flex-col gap-2">
          {[
            `${formatCredits(credits)} credits refresh every month`,
            isAnnual ? 'Annual billing — 2 months free' : 'Cancel or change your plan anytime',
            'Priority access to new features',
          ].map((perk) => (
            <div key={perk} className="flex items-center gap-2.5 text-[12.5px] text-secondary">
              <span className="i-ph:check-circle-fill flex-shrink-0 text-[15px] text-accent" />
              {perk}
            </div>
          ))}
        </div>

        {/* CTA */}
        <button type="button" onClick={dismissPlanWelcome} className="ui-btn-accent mt-5 w-full justify-center">
          Start building
        </button>
      </motion.div>
    </AccessibleModal>
  );
}
